import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  QUERY_ONGOING_PARKING_EVENTS,
  SHORT_CACHE_TIME_MS,
} from '../../apis/moovyQueryKeys'
import {
  ParkingEventDTO,
  ParkingEventsSortType,
} from '../../apis/parkingEvents/dto'
import {
  MoovyGrid,
  MoovyGridCard,
  MoovyScrollToTop,
  MoovySortControl,
  MoovyTable,
  MoovyTableControlLayout,
} from '../../components'
import {
  MoovyPaginationProps,
  PaginationAndSortingValues,
} from '../../components/moovyPagination'
import MoovySearch, {
  DateTimeRangeStringValues,
} from '../../components/moovySearch'
import { prettyDuration, prettyFullTimestamp } from '../../utils/datetimeUtils'
import WhitelistedVehicleIcon, {
  WhitelistedVehicleIconWithLicensePlate,
} from './whitelistedVehicleIcon'

import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDefaultPaginationAndSortingValues } from '../../apis'
import { ParkingEventsApi } from '../../apis/parkingEvents/parkingEventsApi'
import { appRoutes } from '../../app/permissions'
import { getSortItems } from '../../components/moovySortControl/sortValues'

const OngoingParkingEvents = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const defaultSortCriteria: ParkingEventsSortType = 'START'
  const [paginationAndSortingValues, setPaginationAndSortingValues] =
    useState<PaginationAndSortingValues>({
      ...getDefaultPaginationAndSortingValues(defaultSortCriteria),
    })

  type OngoingParkingEventsSearchParameters = {
    licensePlate: string
    start: string | null
    end: string | null
    lane: string
  }

  const [searchParameters, setSearchParameters] =
    useState<OngoingParkingEventsSearchParameters>({
      licensePlate: '',
      start: null,
      end: null,
      lane: '',
    })

  const queryKey = [
    QUERY_ONGOING_PARKING_EVENTS,
    searchParameters.licensePlate,
    searchParameters.start,
    searchParameters.end,
    searchParameters.lane,
    paginationAndSortingValues.page,
    paginationAndSortingValues.sort,
    paginationAndSortingValues.direction,
  ]

  const queryOngoingParkingEvents = useQuery({
    queryKey,
    queryFn: () =>
      ParkingEventsApi.getOngoingParkingEvents({
        paginationAndSorting: paginationAndSortingValues,
        searchParameters,
      }),
    staleTime: SHORT_CACHE_TIME_MS,
  })

  const pagination: MoovyPaginationProps = {
    page: paginationAndSortingValues.page,
    limit: paginationAndSortingValues.limit,
    total: queryOngoingParkingEvents.data?.data.total ?? 0,
    onNextPage: (page) => {
      setPaginationAndSortingValues({
        ...paginationAndSortingValues,
        page,
      })
    },
  }

  const [licensePlateInput, setLicensePlateInput] = useState(
    searchParameters.licensePlate,
  )
  const [dateTimeRangeInput, setDateTimeRangeInput] =
    useState<DateTimeRangeStringValues>({
      start: searchParameters.start,
      end: searchParameters.end,
    })
  const [laneInput, setLaneInput] = useState(searchParameters.lane)

  const onSearch = () => {
    setPaginationAndSortingValues({
      ...paginationAndSortingValues,
      page: 1,
    })
    setSearchParameters({
      licensePlate: licensePlateInput,
      start: dateTimeRangeInput.start,
      end: dateTimeRangeInput.end,
      lane: laneInput,
    })
    // Override cache if search parameters have not been changed
    if (
      searchParameters.licensePlate === licensePlateInput &&
      searchParameters.start === dateTimeRangeInput.start &&
      searchParameters.end === dateTimeRangeInput.end &&
      searchParameters.lane === laneInput
    ) {
      queryClient.invalidateQueries({ queryKey })
    }
  }

  const onClickRow = (parkingEvent: ParkingEventDTO) => {
    navigate(`${appRoutes.PARKING_EVENTS}/${parkingEvent.id}`)
  }

  const parkingEvents: ParkingEventDTO[] =
    queryOngoingParkingEvents.data?.data.data || []
  return (
    <>
      <MoovyScrollToTop />
      <MoovyTableControlLayout
        searchControl={
          <MoovySearch.SearchBar breakpoint="md" onSearch={onSearch}>
            <MoovySearch.TextSearchField
              label={intl.formatMessage({ id: 'common.text.licensePlate' })}
              stateControl={[licensePlateInput, setLicensePlateInput]}
            />
            <MoovySearch.DateTimeRangePickerSearchField
              stateControl={[dateTimeRangeInput, setDateTimeRangeInput]}
            />
            <MoovySearch.TextSearchField
              label={intl.formatMessage({ id: 'common.text.entryLane' })}
              stateControl={[laneInput, setLaneInput]}
            />
          </MoovySearch.SearchBar>
        }
        sortControl={
          <MoovySortControl
            sortOptions={getSortItems(intl, 'PARKING_EVENTS_ONGOING')}
            defaultSortCriteria={defaultSortCriteria}
            paginationAndSortingValues={paginationAndSortingValues}
            setPaginationAndSortingValues={setPaginationAndSortingValues}
          />
        }
      />

      <MoovyGrid
        emptyTextId="page.parkingEvents.ongoing.emptyText"
        errorTextId="common.actions.get.error"
        query={queryOngoingParkingEvents}
        pagination={pagination}
      >
        {parkingEvents.map((parkingEvent) => (
          <MoovyGridCard
            key={parkingEvent.id}
            cardTitle={
              <Typography fontWeight={600}>
                {parkingEvent.parkingType === 'WHITELIST' ? (
                  <WhitelistedVehicleIcon />
                ) : (
                  <></>
                )}
                {parkingEvent.licensePlate}
              </Typography>
            }
            cardSecondaryTitle={
              <Typography>
                <FormattedMessage id="common.text.duration" />:{' '}
                {prettyDuration(intl, parkingEvent)}
              </Typography>
            }
            avatarName={
              parkingEvent.parkingType === 'WHITELIST'
                ? intl.formatMessage({ id: 'common.text.whitelist' })[0]
                : ''
            }
            cardContentList={[
              <Typography>
                <FormattedMessage id="common.text.startTime" />:{' '}
                {prettyFullTimestamp(intl, parkingEvent.startTime)}
              </Typography>,
              <Typography>
                <FormattedMessage id="common.text.entryLane" />:{' '}
                {parkingEvent.lane}
              </Typography>,
            ]}
            onCardClicked={() => onClickRow(parkingEvent)}
          />
        ))}
      </MoovyGrid>
      <MoovyTable
        items={parkingEvents}
        query={queryOngoingParkingEvents}
        pagination={pagination}
        emptyTextId="page.parkingEvents.ongoing.emptyText"
        errorTextId="common.actions.get.error"
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <FormattedMessage id="common.text.licensePlate" />
            </TableCell>
            <TableCell>
              <FormattedMessage id="common.text.startTime" />
            </TableCell>
            <TableCell align="right">
              <FormattedMessage id="common.text.duration" />
            </TableCell>
            <TableCell align="right">
              <FormattedMessage id="common.text.entryLane" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parkingEvents.map((parkingEvent) => (
            <TableRow
              key={parkingEvent.id}
              onClick={() => onClickRow(parkingEvent)}
              hover
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>
                <WhitelistedVehicleIconWithLicensePlate
                  parkingEvent={parkingEvent}
                />
              </TableCell>
              <TableCell>
                <Typography>
                  {prettyFullTimestamp(intl, parkingEvent.startTime)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>{prettyDuration(intl, parkingEvent)}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>{parkingEvent.lane}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </MoovyTable>
    </>
  )
}

export default OngoingParkingEvents
